import { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { motion } from 'framer-motion';
import { Grid3x3 } from 'lucide-react';
import { PortfolioProject, portfolioProjects } from '../data/portfolioProjects';
import InteractiveGlobe from '../components/InteractiveGlobe';
import ProjectDetailView from '../components/ProjectDetailView';

export default function Portfolio2() {
  const [selectedProject, setSelectedProject] = useState<PortfolioProject | null>(null);
  const [hoveredProject, setHoveredProject] = useState<PortfolioProject | null>(null);
  const [use3D, setUse3D] = useState(true);

  // 2D Grid Fallback Component
  const GridView = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {!selectedProject && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {portfolioProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className="bg-night-700 rounded-xl p-6 border border-night-600 hover:border-launch-blue/50 transition-all group cursor-pointer hover:scale-105"
              style={{ borderColor: `${project.color}20` }}
            >
              <div className="relative h-48 mb-4 rounded-lg overflow-hidden" style={{ backgroundColor: `${project.color}20` }}>
                <img 
                  src={project.image} 
                  alt={`${project.title} preview`}
                  className="w-full h-full object-cover object-top transition-transform group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: project.color }}>
                {project.title}
              </h3>
              <p className="text-sm text-launch-purple mb-3">{project.category}</p>
              <p className="text-offwhite/70 text-sm mb-4">{project.description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tech.map((tech) => (
                  <span
                    key={tech}
                    className="text-xs px-2 py-1 rounded bg-night-600 text-offwhite/80"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!use3D) {
    return (
      <div className="min-h-screen bg-night-800 pt-20 pb-20">
        <div className="relative z-10 text-center py-8 px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Our Portfolio
          </h1>
          <p className="text-night-200 text-base md:text-lg max-w-2xl mx-auto mb-6">
            Explore our projects using the 3D interactive globe.
          </p>
          
          {/* Switch to Globe View Button - matches 3D button position */}
          {!selectedProject && (
            <button
              onClick={() => setUse3D(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-night-700/80 backdrop-blur-sm border border-launch-blue/30 text-offwhite hover:border-launch-blue hover:bg-night-600 transition-all shadow-lg"
            >
              🌍 Switch to Globe View
            </button>
          )}
        </div>
        <GridView />
        {selectedProject && (
          <ProjectDetailView
            project={selectedProject}
            onBack={() => setSelectedProject(null)}
            onToggle2D={() => setUse3D(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-night-800 pt-20 pb-20">
      {/* Header */}
      <div className="relative z-10 text-center py-8 px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
          Our Portfolio
        </h1>
        <p className="text-night-200 text-base md:text-lg max-w-2xl mx-auto mb-6">
          Explore our projects using the 3D interactive globe. Click on the markers to learn more.
        </p>
        
        {/* Switch to 2D Button - only show when no project is selected */}
        {!selectedProject && (
          <button
            onClick={() => setUse3D(false)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-night-700/80 backdrop-blur-sm border border-launch-blue/30 text-offwhite hover:border-launch-blue hover:bg-night-600 transition-all shadow-lg"
          >
            <Grid3x3 size={18} />
            <span className="font-medium">Switch to 2D View</span>
          </button>
        )}
      </div>

      {/* Only show globe and tooltips when no project is selected */}
      {!selectedProject && (
        <>
          {/* Hover Tooltip */}
          {hoveredProject && (
            <div className="fixed top-1/2 right-4 md:right-8 transform -translate-y-1/2 z-50 pointer-events-none max-w-xs md:max-w-md">
              <div 
                className="bg-night-800/95 backdrop-blur-md text-white px-5 md:px-8 py-4 md:py-6 rounded-2xl border-4 shadow-2xl animate-in fade-in slide-in-from-right-2 duration-200"
                style={{ borderColor: hoveredProject.color }}
              >
                <div className="font-bold text-xl md:text-3xl mb-2" style={{ color: hoveredProject.color }}>
                  {hoveredProject.title}
                </div>
                <div className="text-night-300 text-base md:text-xl font-medium mb-3">{hoveredProject.category}</div>
                <div className="text-night-200 text-xs md:text-sm">{hoveredProject.description}</div>
              </div>
            </div>
          )}

          {/* Globe Scene */}
          <div className="w-full h-[50vh] md:h-[60vh] relative">
            <Canvas
              camera={{ position: [0, 0, 3], fov: 45 }}
              gl={{ antialias: true, alpha: true }}
            >
              <InteractiveGlobe 
                onProjectClick={setSelectedProject}
                onProjectHover={setHoveredProject}
              />
            </Canvas>

            {/* Instructions overlay */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-night-700/80 backdrop-blur-sm px-4 md:px-6 py-2 md:py-3 rounded-full border border-launch-500/30">
              <p className="text-night-200 text-xs md:text-sm">
                🖱️ Drag to rotate • 🔍 Scroll to zoom • 📍 Click markers for details
              </p>
            </div>
          </div>
        </>
      )}

      {/* Project Detail - replaces the globe when a project is selected */}
      {selectedProject && (
        <ProjectDetailView
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
          onToggle2D={() => window.location.href = '/portfolio'}
        />
      )}
    </div>
  );
}
